import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  ViewEncapsulation
} from '@angular/core';

import { TValue } from 'src/app/models/generic';
import { TemplateVariableService } from 'src/app/services/template-variable.service';
import {
  createTemplateVariablePlugin,
  TEMPLATE_VARIABLE_ELEMENT
} from './template-variable.plugin';
import {
  createTemplateBlockPlugin,
  TEMPLATE_BLOCK_ELEMENT
} from './template-block.plugin';
import { createProtectedWidgetsPlugin } from './protected-widgets.plugin';

import * as Editor from 'src/app/core/libs/ckeditor5/build/ckeditor';
import '@ckeditor/ckeditor5-build-classic/build/translations/pt-br';

type Config = {
  toolbar: string[];
  fontColor: {
    colors: { color: string; label: string; hasBorder?: boolean }[];
  };
  fontBackgroundColor: {
    colors: { color: string; label: string; hasBorder?: boolean }[];
  };
  language: { ui: string; content: string };
  extraPlugins: any[];
  htmlSupport: { allow: any[] };
  mention: {
    dropdownLimit: number;
    feeds: {
      marker: string;
      feed:
        | TTemplateVariable[]
        | ((query: string) => TTemplateVariable[] | Promise<TTemplateVariable[]>);
      minimumCharacters: number;
      itemRenderer?: any;
    }[];
  };
};

/**
 * `id` é a expressão Handlebars persistida (vai para `data-mention`) e `text` é
 * o rótulo amigável exibido no chip e no dropdown.
 */
export type TTemplateVariable = { id: string; text: string };

@Component({
  selector: 'app-c-rich-text',
  templateUrl: './c-rich-text.component.html',
  styleUrls: ['./c-rich-text.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CRichTextComponent)
    }
  ]
})
export class CRichTextComponent
  implements OnChanges, ControlValueAccessor
{
  @Input() placeholder: string = '';
  @Input() value!: TValue;
  @Input() templateVariables: { id: string; text: string }[] = [];
  @Input() templateVariablesLength = 1;

  /**
   * Modo campo de uma linha (usado no Assunto): sem toolbar e sem Enter, mas
   * com os mesmos widgets de variável do corpo — é o que impede o usuário de
   * editar a variável por dentro.
   */
  @Input() singleLine = false;

  /**
   * Desligado na edição de template existente: as variáveis ficam como estão,
   * sem inserir novas. Ligado na criação, senão o template nasceria sem nenhuma.
   */
  @Input() allowInsertVariables = true;

  /** Conteúdo exibido no lugar de cada trecho fixo (ver `previews`). */
  @Input() blockPreview: (blockId: number) => string | null = () => null;

  onChange = (value: TValue): void => {};

  editorFontColors = [
    {
      color: 'hsl(0, 0%, 0%)',
      label: 'Black'
    },
    {
      color: 'hsl(0, 0%, 30%)',
      label: 'Dim grey'
    },
    {
      color: 'hsl(0, 0%, 60%)',
      label: 'Grey'
    },
    {
      color: 'hsl(0, 0%, 90%)',
      label: 'Light grey'
    },
    {
      color: 'hsl(0, 0%, 100%)',
      label: 'White',
      hasBorder: true
    },
    {
      color: 'hsl(0, 75%, 60%)',
      label: 'Red'
    },
    {
      color: 'hsl(30, 75%, 60%)',
      label: 'Orange'
    },
    {
      color: 'hsl(60, 75%, 60%)',
      label: 'Yellow'
    },
    {
      color: 'hsl(90, 75%, 60%)',
      label: 'Light green'
    },
    {
      color: 'hsl(120, 75%, 60%)',
      label: 'Green'
    }
  ];

  // O build do CKEditor é CommonJS. No builder `application` (esbuild, Angular
  // 17+), `import * as` devolve um namespace com a classe em `default`, e
  // passar o namespace ao <ckeditor> quebra o `create` ("this is not a
  // constructor"). No webpack, `default` não existe e vale o próprio `Editor`.
  public Editor = ((Editor as any).default ?? Editor) as any;

  editorConfig!: Config;

  String = String;

  editorInstance: any;

  constructor(private _templateVariableService: TemplateVariableService) {}

  ngOnChanges(): void {
    this.setEditorConfig();
  }

  setEditorConfig(): void {
    this.editorConfig = {
      fontColor: {
        colors: this.editorFontColors
      },
      fontBackgroundColor: {
        colors: this.editorFontColors
      },
      toolbar: this.singleLine
        ? []
        : [
            'bold',
            'italic',
            'fontColor',
            'fontBackgroundColor',
            'fontSize',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'undo',
            'redo'
          ],
      language: { ui: 'pt-br', content: 'pt-br' },
      // Transforma as variáveis em widgets atômicos (não editáveis por dentro
      // e que acompanham a célula em operações de tabela).
      extraPlugins: [
        createTemplateVariablePlugin((expression) =>
          this._templateVariableService.label(expression)
        ),
        // Trechos fixos do template (tabela, `{{#if}}`...) — ver
        // TemplateBodyLockService.
        createTemplateBlockPlugin((blockId) => this.blockPreview(blockId)),
        // Selecionar tudo e apagar limpa o texto, mas não os widgets acima.
        createProtectedWidgetsPlugin([
          TEMPLATE_VARIABLE_ELEMENT,
          TEMPLATE_BLOCK_ELEMENT
        ])
      ],
      // O GeneralHtmlSupport já vem no bundle, mas sem esta lista não preserva
      // nada: o estilo inline dos templates (cor, fonte, alinhamento) sumia ao
      // abrir o editor.
      //
      // Sem `classes`: o GHS envolveria cada chip de variável num
      // `<span class="cred-var">` extra, que se aninharia a cada save. Templates
      // de e-mail usam estilo inline, não classe.
      htmlSupport: {
        allow: [
          {
            name: /^(p|span|strong|b|em|i|u|s|a|br|div|h[1-6]|ul|ol|li|blockquote|font|center|small|sub|sup)$/,
            styles: true,
            attributes: [
              {
                key: /^(href|target|title|align|dir|color|face|size)$/,
                value: true
              }
            ]
          }
        ]
      },
      // Sem feed, digitar `{` não abre o menu e não há como inserir variável.
      mention: {
        dropdownLimit: this.templateVariables.length + 1,
        feeds: !this.allowInsertVariables
          ? []
          : [
              {
                marker: '{',

                feed: (query: string) => this.filterVariables(query),
                minimumCharacters: 1,
                itemRenderer: (item: TTemplateVariable) =>
                  this.renderVariableItem(item)
              }
            ]
      }
    };
  }

  /**
   * Busca tanto pelo rótulo amigável ("valor total") quanto pelo nome técnico
   * ("AmountTotal"), para quem já está acostumado com a sintaxe antiga.
   */
  filterVariables(query: string): TTemplateVariable[] {
    const term = query.trim().toLowerCase();

    if (!term) return this.templateVariables;

    return this.templateVariables.filter(
      (variable) =>
        variable.text.toLowerCase().includes(term) ||
        variable.id.toLowerCase().includes(term)
    );
  }

  /**
   * No modo de uma linha, Enter não pode quebrar o campo em dois parágrafos —
   * o valor precisa continuar sendo uma linha só de texto.
   */
  onEditorReady(editor: any): void {
    this.editorInstance = editor;

    if (!this.singleLine) return;

    editor.editing.view.document.on(
      'enter',
      (event: any, data: any) => {
        data.preventDefault();
        event.stop();
      },
      { priority: 'highest' }
    );
  }

  renderVariableItem(item: TTemplateVariable): HTMLElement {
    const element = document.createElement('span');

    element.classList.add('c-rich-text__variable-option');
    element.textContent = item.text;

    return element;
  }

  writeValue(value: TValue): void {
    this.value = value;
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
